import React, { createContext, useContext, useState, useEffect } from "react";
import io from "socket.io-client";
import { useAuth } from "@clerk/clerk-react";

const SocketContext = createContext();

export const useSocket = () => useContext(SocketContext);

export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);
  const { userId, isSignedIn } = useAuth();

  useEffect(() => {
    // Determine socket URL based on current origin
    let socketUrl;
    
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      // Localhost: use http://localhost:5000
      socketUrl = 'http://localhost:5000';
    } else if (window.location.hostname.includes('vercel.app')) {
      // Vercel: use https://bookmycinema.onrender.com
      socketUrl = 'https://bookmycinema.onrender.com';
    } else {
      // Custom domain or other: use current origin
      socketUrl = window.location.origin;
    }
    
    console.log('🔌 Connecting to socket:', socketUrl);
    const socketInstance = io(socketUrl, {
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5
    });

    socketInstance.on("connect", () => {
      console.log("✅ Socket connected");
      setConnected(true);

      // Register user ID with socket server when connected
      if (isSignedIn && userId) {
        socketInstance.emit("register-user", userId);
        console.log("Registered user ID with socket server:", userId);
      }
    });

    // Re-register user ID if auth state changes
    if (isSignedIn && userId && connected && socket) {
      socket.emit("register-user", userId);
      console.log("Re-registered user ID with socket server:", userId);
    }

    socketInstance.on("connect_error", (error) => {
      console.error("❌ Socket connection error:", error);
    });

    socketInstance.on("error", (error) => {
      console.error("❌ Socket error:", error);
    });

    socketInstance.on("disconnect", () => {
      console.log("⏹️ Socket disconnected");
      setConnected(false);
    });

    socketInstance.on("movie-added", (movie) => {
      window.dispatchEvent(new CustomEvent("movies-updated"));
    });

    socketInstance.on("movie-updated", (movie) => {
      window.dispatchEvent(new CustomEvent("movies-updated"));
    });

    socketInstance.on("movie-deleted", (data) => {
      window.dispatchEvent(new CustomEvent("movies-updated"));
    });

    socketInstance.on("show-added", (show) => {
      window.dispatchEvent(new CustomEvent("shows-updated"));
    });

    socketInstance.on("show-updated", (show) => {
      window.dispatchEvent(new CustomEvent("shows-updated"));
    });

    socketInstance.on("show-deleted", (data) => {
      window.dispatchEvent(new CustomEvent("shows-updated"));
    });

    socketInstance.on("theater-added", (theater) => {
      window.dispatchEvent(new CustomEvent("theaters-updated"));
    });

    socketInstance.on("theater-updated", (theater) => {
      window.dispatchEvent(new CustomEvent("theaters-updated"));
    });

    socketInstance.on("theater-deleted", (data) => {
      window.dispatchEvent(new CustomEvent("theaters-updated"));
    });

    // Seat booking events
    socketInstance.on("seat-conflict", (data) => {
      if (data.userId === userId) {
        window.dispatchEvent(
          new CustomEvent("seat-conflict", { detail: data })
        );
        console.log("Received seat conflict notification for this user:", data);
      }
    });

    socketInstance.on("seats-auto-released", (data) => {
      if (data.userId === userId) {
        window.dispatchEvent(
          new CustomEvent("seats-auto-released", { detail: data })
        );
        console.log("Seats auto-released for this user:", data);
      }
    });

    socketInstance.on("seats-available", (data) => {
      window.dispatchEvent(
        new CustomEvent("seats-available", { detail: data })
      );
    });

    socketInstance.on("seats-selected", (data) => {
      if (data.userId !== userId) {
        window.dispatchEvent(new CustomEvent("seats-selected", { detail: data }));
        console.log("Other user selected seats:", data);
      }
    });

    socketInstance.on("seats-locked", (data) => {
      if (data.userId !== userId) {
        window.dispatchEvent(new CustomEvent("seats-locked", { detail: data }));
        console.log("Other user locked seats:", data);
      }
    });

    socketInstance.on("seats-released", (data) => {
      window.dispatchEvent(new CustomEvent("seats-released", { detail: data }));
      console.log("Seats released:", data);
    });

    socketInstance.on("seats-booked", (data) => {
      window.dispatchEvent(new CustomEvent("seats-booked", { detail: data }));
    });

    socketInstance.on("seat-status-sync", (data) => {
      window.dispatchEvent(new CustomEvent("seat-status-sync", { detail: data }));
      console.log("Seat status synced:", data);
    });

    socketInstance.on("seats-cleanup", (data) => {
      window.dispatchEvent(new CustomEvent("seats-cleanup", { detail: data }));
      console.log("Seats cleaned up:", data);
    });

    // Request current seat status on connect to handle page refresh
    socketInstance.on("connect", () => {
      setTimeout(() => {
        socketInstance.emit('request-seat-status');
      }, 1000);
    });

    setSocket(socketInstance);

    return () => {
      socketInstance.disconnect();
    };
  }, [userId, isSignedIn]);

  return (
    <SocketContext.Provider value={{ socket, connected }}>
      {children}
    </SocketContext.Provider>
  );
};

export default SocketContext;
